import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import useGalleryEnabled from "../lib/useGalleryEnabled";
import { useCompany } from "../context/CompanyContext";

/**
 * Hamburger menu used in the public marketing site (Home + 4 inner pages).
 * Rendered fixed in the top-left corner. When opened it slides in from the
 * left as a full-height overlay panel with the allowed links:
 *   Solução · Oferta · Limpeza incluída · História · (Galeria)
 * The Galeria entry is only rendered when the admin has toggled the public
 * gallery ON (iter39 — see /api/gallery/status).
 */
export default function HamburgerMenu({ open, onOpenChange }) {
  const { company, logoDark } = useCompany();
  const galleryEnabled = useGalleryEnabled();
  // Lock the body scroll while the panel is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onOpenChange(false); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const links = [
    { to: "/", label: "Início" },
    { to: "/precos", label: "Preços" },
    { to: "/contacto", label: "Contacte-nos" },
    { to: "/solucao", label: "Solução" },
    { to: "/oferta", label: "Tudo incluído" },
    { to: "/limpeza-incluida", label: "Porquê escolher-nos" },
    { to: "/historia", label: "História" },
    ...(galleryEnabled ? [{ to: "/galeria", label: "Galeria" }] : []),
    { to: "/pagamentos", label: "Pagamentos" },
  ];

  return (
    <>
      <button onClick={() => onOpenChange(true)}
              aria-label="Abrir menu"
              className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-[#031411] border border-[#d4af37]/70 text-[#d4af37] shadow-[0_2px_10px_rgba(0,0,0,0.35)] hover:bg-[#d4af37] hover:text-[#031411] hover:border-[#d4af37] transition-colors focus:outline-none focus:ring-2 focus:ring-[#d4af37]/70"
              data-testid="hamburger-open-btn">
        <Menu size={22} strokeWidth={2.4} />
      </button>

      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      <aside
        className={`fixed top-0 left-0 bottom-0 w-[88%] max-w-sm z-50 bg-[#031411] text-[#fdfbf7] shadow-2xl border-r border-[#d4af37]/20 transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu principal"
        data-testid="hamburger-panel">
        <div className="flex items-center justify-between p-5 border-b border-[#1a4640]">
          <span className="bg-[#fdfbf7] rounded-sm px-2.5 py-1 inline-flex items-center">
            <img src={logoDark}
                 alt={company.trade_name}
                 className="h-14 w-auto select-none"
                 loading="eager" decoding="async" />
          </span>
          <button onClick={() => onOpenChange(false)}
                  aria-label="Fechar menu"
                  className="w-10 h-10 inline-flex items-center justify-center rounded-sm hover:bg-[#d4af37]/10 hover:text-[#d4af37] focus:outline-none focus:ring-2 focus:ring-[#d4af37]/60"
                  data-testid="hamburger-close-btn">
            <X size={20} />
          </button>
        </div>
        <nav className="px-2 py-4">
          <ul className="space-y-1">
            {links.map(l => {
              // Hash targets (#precos, #contacto, …) need the browser's
              // native anchor behaviour to scroll to the section. React
              // Router's <Link to="/#precos"> on the same route only
              // updates location.hash without scrolling.
              const isHash = l.to.includes("#");
              const testid = `hamburger-link-${
                l.to === "/" ? "home"
                : isHash ? l.to.split("#").pop()
                : l.to.replace("/", "")
              }`;
              const cls = "block px-4 py-4 rounded-sm text-lg font-display tracking-tight hover:bg-[#d4af37]/10 hover:text-[#d4af37] focus:outline-none focus:ring-2 focus:ring-[#d4af37]/60";
              if (isHash) {
                return (
                  <li key={l.to}>
                    <a href={l.to}
                       onClick={() => onOpenChange(false)}
                       className={cls}
                       data-testid={testid}>
                      {l.label}
                    </a>
                  </li>
                );
              }
              return (
                <li key={l.to}>
                  <Link to={l.to}
                        onClick={() => onOpenChange(false)}
                        className={cls}
                        data-testid={testid}>
                    {l.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-[#1a4640] text-xs text-[#a0b2af]/70 pointer-events-none">
          © {new Date().getFullYear()} · {company.trade_name} · {company.city || "Funchal"}
        </div>
      </aside>
    </>
  );
}
